import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { useTheme } from '@mui/material/styles';
import { 
  AgentWizard, 
  AgentStatusOverview 
} from '../AgentStudio/CreatingAgents';
import { 
  AgentOverview, 
  DeleteAgentDialog 
} from '../AgentStudio/ManagingAgents';
import { getAgentList, getAllAgents, deleteAgent } from '../api/services/agent-studio/AgentManagement';
import { getMyAgents } from '../api/services/agent-studio/AgentUserManagement';
import { validateApiKey } from '../api/services/agent-studio/AgentFeatures';
import { useUser } from '../../contexts/UserContext';

const AgentStudio = () => {
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const { onlyServiceRoleUser, user } = useUser();

  const [showWizard, setShowWizard] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [createdAgents, setCreatedAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [agentConfig, setAgentConfig] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const fetchCreatedAgents = async () => {
    setIsLoading(true);
    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;
      const response = await getAgentList(token);
      
      if (response.success && response.data) {
        const agents = Array.isArray(response.data.agents) ? response.data.agents : response.data;
        const sortedAgents = agents.sort((a, b) => {
          const dateA = a.createdAt || a.created_at || '';
          const dateB = b.createdAt || b.created_at || '';
          return new Date(dateB) - new Date(dateA);
        });
        setCreatedAgents(sortedAgents);
      }
    } catch (error) {
      console.error("Error fetching created agents:", error);
      setCreatedAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllAgents = async () => {
    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;
      const response = await getAllAgents(token);
      
      if (response.success && response.data) {
        const agents = Array.isArray(response.data.agents) ? response.data.agents : response.data;
        setAllAgents(agents);
      } else {
        setAllAgents([]);
      }
    } catch (error) {
      console.error("Error fetching all agents:", error);
      setAllAgents([]);
    }
  };

  const fetchAssignedAgents = async () => {
    if (!currentUserId) return;
    
    setIsLoadingAssigned(true);
    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;
      const response = await getMyAgents(token, currentUserId);
      
      if (response.success && response.agents) {
        const transformedAgents = response.agents.map((myAgent) => {
          const agent = myAgent.agent || myAgent;
          return {
            ...agent,
            api_key: myAgent.api_key,
            assigned_role: myAgent.role,
            isAssigned: true
          };
        }).sort((a, b) => {
          const dateA = a.createdAt || a.created_at || '';
          const dateB = b.createdAt || b.created_at || '';
          return new Date(dateB) - new Date(dateA);
        });
        
        if (onlyServiceRoleUser) {
          setAssignedAgents(transformedAgents);
        } else {
          const allAgentIds = new Set(allAgents.map(agent => agent.id));
          const filteredAgents = transformedAgents.filter(assignedAgent => {
            return !allAgentIds.has(assignedAgent.id);
          });
          setAssignedAgents(filteredAgents);
        }
      } else {
        setAssignedAgents([]);
      }
    } catch (error) {
      console.error("Error fetching assigned agents:", error);
      setAssignedAgents([]);
    } finally {
      setIsLoadingAssigned(false);
    }
  };


  useEffect(() => {
    const loadInitialData = async () => {
      if (!keycloak.authenticated || !user) {
        setCreatedAgents([]);
        setAllAgents([]);
        setAssignedAgents([]);
        return;
      }

      try {
        setCurrentUserId(user.id);
        const token = keycloak.token;

        if (onlyServiceRoleUser) {
          setIsLoading(true);
          const myAgentsResponse = await getMyAgents(token, user.id);
          if (myAgentsResponse.success && myAgentsResponse.agents) {
            const transformedAgents = myAgentsResponse.agents.map((myAgent) => {
              const agent = myAgent.agent || myAgent;
              return {
                ...agent,
                api_key: myAgent.api_key,
                assigned_role: myAgent.role,
                isAssigned: true
              };
            }).sort((a, b) => {
              const dateA = a.createdAt || a.created_at || '';
              const dateB = b.createdAt || b.created_at || '';
              return new Date(dateB) - new Date(dateA);
            });
            setAssignedAgents(transformedAgents);
          }
          setIsLoading(false);
        } else {
          const [createdResponse, allAgentsResponse] = await Promise.all([
            getAgentList(token),
            getAllAgents(token)
          ]);

          if (createdResponse.success && createdResponse.data) {
            const agents = Array.isArray(createdResponse.data.agents) ? createdResponse.data.agents : createdResponse.data;
            const sortedAgents = agents.sort((a, b) => {
              const dateA = a.createdAt || a.created_at || '';
              const dateB = b.createdAt || b.created_at || '';
              return new Date(dateB) - new Date(dateA);
            });
            setCreatedAgents(sortedAgents);
          }

          if (allAgentsResponse.success && allAgentsResponse.data) {
            const agents = Array.isArray(allAgentsResponse.data.agents) ? allAgentsResponse.data.agents : allAgentsResponse.data;
            setAllAgents(agents);
          }
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, [keycloak.authenticated, onlyServiceRoleUser, user]);

  useEffect(() => {
    if (!keycloak.authenticated || !currentUserId) return;
    
    const loadAssignedAgents = async () => {
      setIsLoadingAssigned(true);
      try {
        await keycloak.updateToken(30);
        const token = keycloak.token;
        const response = await getMyAgents(token, currentUserId);
        
        if (response.success && response.agents) {
          const transformedAgents = response.agents.map((myAgent) => {
            const agent = myAgent.agent || myAgent;
            return {
              ...agent,
              apiKey: myAgent.apiKey || myAgent.api_key || '',
              api_key: myAgent.apiKey || myAgent.api_key || '',
              assigned_role: myAgent.role,
              isAssigned: true
            };
          }).sort((a, b) => {
            const dateA = a.createdAt || a.created_at || '';
            const dateB = b.createdAt || b.created_at || '';
            return new Date(dateB) - new Date(dateA);
          });
          
          if (onlyServiceRoleUser) {
            setAssignedAgents(transformedAgents);
          } else {
            const allAgentIds = new Set(allAgents.map(agent => agent.id));
            const filteredAgents = transformedAgents.filter(assignedAgent => {
              return !allAgentIds.has(assignedAgent.id);
            });
            setAssignedAgents(filteredAgents);
          }
        } else {
          setAssignedAgents([]);
        }
      } catch (error) {
        console.error("Error fetching assigned agents:", error);
        setAssignedAgents([]);
      } finally {
        setIsLoadingAssigned(false);
      }
    };

    if (onlyServiceRoleUser) {
      loadAssignedAgents();
    } else if (allAgents.length > 0) {
      loadAssignedAgents();
    }
  }, [keycloak.authenticated, currentUserId, allAgents, onlyServiceRoleUser]);

  const handleStartAgent = () => {
    setShowWizard(true);
  };

  const handleCompleteAgent = (agentConfig) => {
    if (agentConfig.apiResponse) {
      fetchCreatedAgents();
      fetchAllAgents();
    }
    
    setShowWizard(false);
    setAgentConfig(null);
    setActiveStep(0);
  };

  const handleAgentUpdate = (config, step) => {
    setAgentConfig(config);
    setActiveStep(step);
  };


  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;
    
    setIsLoading(true);
    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;
      
      await deleteAgent(token, agentToDelete.id);
      await fetchCreatedAgents();
      await fetchAllAgents();
    } catch (error) {
      console.error("Error deleting agent:", error);
    } finally {
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (onlyServiceRoleUser) {
      fetchAssignedAgents();
    } else if (activeTab === 0) {
      fetchCreatedAgents();
      fetchAllAgents();
    } else if (activeTab === 1) {
      fetchAllAgents();
      fetchAssignedAgents();
    }
  };

  const handleDeleteClick = (agent) => {
    if (agent.isAssigned) {
      return;
    }
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const getCurrentAgents = () => {
    return activeTab === 0 ? createdAgents : assignedAgents;
  };

  const getCurrentLoading = () => {
    return activeTab === 0 ? isLoading : isLoadingAssigned;
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };

  const handleCancelWizard = () => {
    setShowWizard(false);
    setAgentConfig(null);
    setActiveStep(0);
  };

  const getAgentsForOnlyServiceRoleUser = () => {
    return assignedAgents;
  };

  return (
    <Box sx={{ px: 3, pt: 2, pb: 3 }}>
      {!onlyServiceRoleUser && (
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)} 
            sx={{ 
              mb: 3,
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: theme.palette.primary.main
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main
              }
            }}
          >
            <Tab label={`My Agents (${createdAgents.length})`} />
            <Tab label={`Assigned to Me (${assignedAgents.length})`} />
          </Tabs>
        </Box>
      )}

      <AgentOverview 
        onLaunchAgent={onlyServiceRoleUser ? null : (activeTab === 0 ? handleStartAgent : null)}
        activeAgents={onlyServiceRoleUser ? getAgentsForOnlyServiceRoleUser() : getCurrentAgents()}
        onRefresh={handleRefresh}
        onDelete={onlyServiceRoleUser ? null : handleDeleteClick}
        isLoading={onlyServiceRoleUser ? isLoading : getCurrentLoading()}
      />
      
      <AgentWizard 
        open={showWizard}
        onComplete={handleCompleteAgent}
        onCancel={handleCancelWizard}
      />
      
      <DeleteAgentDialog
        open={deleteDialogOpen}
        agent={agentToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default AgentStudio; 